// Spectating another player on the server: the snapshots carry the followed
// player's full state (FollowState), which is interpolated with the same
// delay as other players. Its movers are restored from the snapshot into a
// MoverSet of our own and drawn at the interpolated command time.

import type { MapLogic } from '../shared/game/mapLogic';
import type { MoverSet } from '../shared/game/movers';
import type { FollowState } from '../shared/net/protocol';
import type { Vec3 } from '../shared/math';

/** Same delay as other players (remotePlayers.ts). */
export const FOLLOW_DELAY = 100;
/** A jump between two snapshots longer than this is a teleport. */
const TELEPORT_DIST = 128;

interface Sample {
  time: number;
  state: FollowState;
}

export interface FollowFrame {
  state: FollowState;
  origin: Vec3;
  viewheight: number;
  angles: Vec3;
  /** The followed player's command time at the drawn moment (movers, timer). */
  commandTime: number;
}

function angleLerp(a: number, b: number, k: number): number {
  let d = b - a;
  while (d > 180) d -= 360;
  while (d < -180) d += 360;
  return a + d * k;
}

export class FollowView {
  private samples: Sample[] = [];
  readonly movers: MoverSet;
  private moversOf: FollowState | null = null;

  constructor(logic: MapLogic) {
    this.movers = logic.createMovers();
  }

  /** The followed player (id), or null before the first snapshot. */
  get target(): number | null {
    return this.samples.length ? this.samples[this.samples.length - 1].state.id : null;
  }

  get name(): string {
    return this.samples.length ? this.samples[this.samples.length - 1].state.name : '';
  }

  add(state: FollowState, now: number): void {
    // a new target starts over
    if (this.samples.length && this.samples[this.samples.length - 1].state.id !== state.id) this.samples = [];
    this.samples.push({ time: now, state });
    if (this.samples.length > 20) this.samples.shift();
  }

  /** The interpolated view at `now` (null before the first snapshot). */
  frame(now: number): FollowFrame | null {
    const s = this.samples;
    if (!s.length) return null;
    const t = now - FOLLOW_DELAY;
    let ai = 0;
    let bi = 0;
    for (let i = 0; i < s.length; i++) {
      if (s[i].time <= t) ai = i;
      bi = i;
      if (s[i].time >= t) break;
    }
    const a = s[ai].state;
    const b = s[bi].state;
    let k = s[bi].time > s[ai].time ? Math.min(1, Math.max(0, (t - s[ai].time) / (s[bi].time - s[ai].time))) : 1;
    const pa = a.ps.origin;
    const pb = b.ps.origin;
    if (Math.hypot(pb[0] - pa[0], pb[1] - pa[1], pb[2] - pa[2]) > TELEPORT_DIST) k = k < 1 ? 0 : 1;
    const origin: Vec3 = [pa[0] + (pb[0] - pa[0]) * k, pa[1] + (pb[1] - pa[1]) * k, pa[2] + (pb[2] - pa[2]) * k];
    const angles: Vec3 = [angleLerp(a.ps.viewangles[0], b.ps.viewangles[0], k), angleLerp(a.ps.viewangles[1], b.ps.viewangles[1], k), 0];
    const viewheight = a.ps.viewheight + (b.ps.viewheight - a.ps.viewheight) * k;
    const commandTime = a.ps.commandTime + (b.ps.commandTime - a.ps.commandTime) * k;
    // the newer snapshot's movers: their trajectories give the poses in between
    if (this.moversOf !== b) {
      this.moversOf = b;
      this.movers.restore(b.movers);
    }
    return { state: k < 0.5 ? a : b, origin, viewheight, angles, commandTime };
  }

  clear(): void {
    this.samples = [];
    this.moversOf = null;
  }
}
